import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  Renderer2,
  ViewChild
} from '@angular/core';
import { BusinessFilter } from 'src/app/models/register-business';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { BusinessService } from 'src/app/services/business.service';
import { PageTitleService } from 'src/app/services/page-title.service';

@Component({
  selector: 'app-listing',
  templateUrl: './listing.component.html',
  styleUrls: ['./listing.component.scss'],
})
export class ListingComponent implements AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  activePanel = 1;
  active = 'place';
  activeInner = 'type-of-meal';
  isSearchBarActive = false;
  // The document title follows the place being browsed; a setter catches every
  // assignment (restored state, saved location, search bar emission).
  private _city: any;
  get city(): any {
    return this._city;
  }
  set city(value: any) {
    this._city = value;
    const place = (value?.city ?? '').toString().trim();
    this.pageTitle.set(place ? `Restaurants in ${place}` : 'Restaurants');
  }
  resturantCoordinates: any;
  filter: BusinessFilter = new BusinessFilter();
  currentCenter: any;
  isInitial: boolean = true;
  searchIds: any[] = [];
  isLoading: boolean = false;
  private restoredFromReturnNavigation: boolean = false;

  // Mobile: the list sheet slides up over the fixed map and is stopped by CSS `position: sticky`
  // with the map's floor still showing. Until it has stopped it must not be a scroll container,
  // or a drag on the list would scroll the list instead of sliding the sheet — so the class that
  // makes it scrollable is applied only once it is resting on the floor.
  @ViewChild('listSheet') private listSheet?: ElementRef<HTMLElement>;
  private sheetFloorTop = NaN;
  private sheetIsFloored = false;
  private readonly syncSheetFloor = () => this.applySheetFloorState();
  private readonly remeasureSheetFloor = () => {
    this.measureSheetFloor();
    this.applySheetFloorState();
  };

  constructor(
    private elRef: ElementRef,
    private userService: UserService,
    private businessService: BusinessService,
    private zone: NgZone,
    private renderer: Renderer2,
    private pageTitle: PageTitleService,
  ) { }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isSearchBarActive = false;
    }
  }

  @HostListener('window:beforeunload')
  handleBeforeUnload() {
    this.persistListingViewState();
  }

  // Toggle View Function
  gridView: boolean = true;

  toggleGridView() {
    this.gridView = true;
  }

  toggleLineView() {
    this.gridView = false;
  }

  ngOnInit(): void {
    // Marks the screen so the footer can be dropped on mobile: anything after the sheet would
    // add page scroll past the map's floor, and it sits behind the opaque sheet regardless.
    this.renderer.addClass(document.body, 'listing-page-body');

    const restored = this.restoreListingViewState();
    if (!restored) {
      const dashboardFilter = this.businessService.filterDataObject?.filter;
      const hasDashboardLocation = dashboardFilter?.origin_Lat != null && dashboardFilter?.origin_long != null;
      const hasDashboardSearch = dashboardFilter?.search && dashboardFilter.search.length >= 3;
      if (!this.businessService.hasListingReturnPending() && (hasDashboardSearch || hasDashboardLocation)) {
        this.filter = Object.assign({}, dashboardFilter);
        const dashboardCity = this.businessService.filterDataObject?.city;
        if (dashboardCity && hasDashboardLocation) {
          this.city = {
            city: dashboardCity,
            lat: dashboardFilter.origin_Lat,
            lng: dashboardFilter.origin_long,
            enableZoomFallBack: true
          };
        }
        this.isInitial = false;
        this.isLoading = false;
        return;
      }
      if (!this.businessService.hasListingReturnPending()) {
        this.businessService.clearListingState();
      }
      this.isLoading = true;
      this.userService.getCurrentLatLng().then((position) => {
        this.isLoading = false;
        this.filter.origin_Lat = position.lat;
        this.filter.origin_long = position.lng;
      },
        (error) => {
          this.isLoading = false;
          throw error;
        });
    }
  }

  updateResturantCoordinates(event) {
    // Only update when the marker set actually changed. Identity + order of
    // (restaurantId, lat, lng) is what the map consumes; the old code serialised
    // both full lists with JSON.stringify on every load and then deep-cloned.
    // A fresh array reference is still assigned so OnPush children re-render.
    if (!this.sameCoordinateSets(this.resturantCoordinates, event)) {
      this.resturantCoordinates = Array.isArray(event) ? [...event] : event;
    }
    this.persistListingViewState();
  }

  private sameCoordinateSets(a: any, b: any): boolean {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.restaurantId !== b[i]?.restaurantId ||
          a[i]?.lat !== b[i]?.lat ||
          a[i]?.lng !== b[i]?.lng ||
          a[i]?.listingIndex !== b[i]?.listingIndex) {
        return false;
      }
    }
    return true;
  }

  filterChanges(filter: BusinessFilter) {
    if (this.city && (!this.currentCenter || this.currentCenter?.lat == null || this.currentCenter?.lng == null)) {
      filter.origin_Lat = this.city?.lat;
      filter.origin_long = this.city?.lng;
      filter.enableZoomFallBack = this.city?.enableZoomFallBack;
    }
    if (this.isInitial && !filter?.origin_Lat && !filter?.origin_long && !this.city) {
      this.userService.getCurrentLatLng().then((position) => {
        this.filter.origin_Lat = position.lat;
        this.filter.origin_long = position.lng;
      },
        (error) => {
          throw error;
        });
      this.isInitial = false;
    }
    if (this.isInitial) {
      this.isInitial = false;
    }
    if ((filter?.origin_Lat == null || filter?.origin_Lat == undefined) && (filter?.origin_long == null || filter?.origin_long == undefined)) {
      filter.origin_Lat = this.filter.origin_Lat;
      filter.origin_long = this.filter.origin_long;
    }
    this.filter = Object.assign({}, filter);
    this.persistListingViewState();
    if (this.currentCenter && this.currentCenter.zoom) {
      if (this.currentCenter.lat !== this.filter.origin_Lat || this.currentCenter.lng !== this.filter.origin_long) {
        this.currentCenter.lat = this.filter.origin_Lat;
        this.currentCenter.lng = this.filter.origin_long;
      }
      this.currentCenter.zoom = this.filter.zoomLevel;
      this.currentCenter.north = this.filter.north;
      this.currentCenter.south = this.filter.south;
      this.currentCenter.east = this.filter.east;
      this.currentCenter.west = this.filter.west;
      this.currentCenter.enableZoomFallBack = this.filter.enableZoomFallBack;
    }
  }

  cityEmitted($event) {
    if (this.city?.lat !== $event?.lat || this.city?.lng !== $event?.lng) {
      this.city = $event;
      this.filter.origin_Lat = this.city?.lat;
      this.filter.origin_long = this.city?.lng;
      this.filter.enableZoomFallBack = this.city?.enableZoomFallBack;
      // Flat scalars; the spread gives the OnPush child a new reference without serialising.
      this.filter = { ...this.filter };
    }
    this.persistListingViewState();
  }

  onCenterChange($event) {
    this.currentCenter = $event;
    this.persistListingViewState();
  }

  showSearchChanges($event) {
    this.searchIds = $event;
    if (Array.isArray(this.searchIds) && this.searchIds.length > 0 && this.filter?.search) {
      this.filter = Object.assign({}, this.filter, { search: '' });
    }
    this.persistListingViewState();
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'listing-page-body');
    window.removeEventListener('scroll', this.syncSheetFloor);
    window.removeEventListener('resize', this.remeasureSheetFloor);
    this.persistListingViewState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Viewport position the sheet comes to rest at. NaN on desktop, where the whole mechanism
   *  is off and the page scrolls normally. */
  private measureSheetFloor() {
    if (typeof window === 'undefined' || window.innerWidth >= 768) {
      this.sheetFloorTop = NaN;
      this.listSheet?.nativeElement.classList.remove('is-floored');
      this.sheetIsFloored = false;
      return;
    }

    const root = getComputedStyle(document.documentElement);
    const header = parseFloat(root.getPropertyValue('--hungery-header-offset'));
    const floor = parseFloat(root.getPropertyValue('--hungery-listing-map-floor'));
    this.sheetFloorTop = (isFinite(header) ? header : 68) + (isFinite(floor) ? floor : 150);
  }

  private applySheetFloorState() {
    const el = this.listSheet?.nativeElement;
    if (!el || !isFinite(this.sheetFloorTop)) {
      return;
    }

    const floored = el.getBoundingClientRect().top <= this.sheetFloorTop + 1;
    if (floored !== this.sheetIsFloored) {
      this.sheetIsFloored = floored;
      el.classList.toggle('is-floored', floored);
    }
  }

  ngAfterViewInit(): void {
    // Outside Angular and passive: this only toggles one class, and must not run change
    // detection on every scroll tick.
    this.zone.runOutsideAngular(() => {
      this.remeasureSheetFloor();
      window.addEventListener('scroll', this.syncSheetFloor, { passive: true });
      window.addEventListener('resize', this.remeasureSheetFloor, { passive: true });
    });

    if (this.restoredFromReturnNavigation) {
      setTimeout(() => {
        this.businessService.clearListingReturnPending();
        this.restoredFromReturnNavigation = false;
      });
      return;
    }
    setTimeout(() => {
      if (!this.businessService.hasListingReturnPending()) {
        this.businessService.filterDataObject = null;
      }
    });
  }

  private restoreListingViewState(): boolean {
    const dashboardFilter = this.businessService.filterDataObject?.filter;
    const hasDashboardLocation = dashboardFilter?.origin_Lat != null && dashboardFilter?.origin_long != null;
    const hasDashboardSearch = !!dashboardFilter?.search;
    if (!this.businessService.hasListingReturnPending() && (hasDashboardSearch || hasDashboardLocation)) {
      return false;
    }
    if (!this.businessService.hasListingReturnPending()) {
      return false;
    }
    const storedState = this.businessService.hydrateListingState();
    if (!storedState) return false;

    if (typeof storedState.activePanel === 'number') {
      this.activePanel = storedState.activePanel;
    }
    if (typeof storedState.gridView === 'boolean') {
      this.gridView = storedState.gridView;
    }
    if (storedState.currentCenter) {
      this.currentCenter = storedState.currentCenter;
    }
    if (Array.isArray(storedState.searchIds)) {
      this.searchIds = storedState.searchIds;
    }
    const hasSearchIds = Array.isArray(this.searchIds) && this.searchIds.length > 0;
    const hasFilters = this.businessService.shouldRestoreListingState(storedState);
    if (hasFilters && Array.isArray(storedState.resturantCoordinates)) {
      this.resturantCoordinates = structuredClone(storedState.resturantCoordinates);
    }
    if (hasFilters) {
      if (storedState.filterDataObject?.filter) {
        const restoredFilter = Object.assign({}, storedState.filterDataObject.filter);
        if (hasSearchIds && restoredFilter.search) {
          restoredFilter.search = '';
        }
        this.filter = restoredFilter;
      } else if (storedState.currentCenter?.lat != null && storedState.currentCenter?.lng != null) {
        this.filter.origin_Lat = storedState.currentCenter.lat;
        this.filter.origin_long = storedState.currentCenter.lng;
        if (storedState.currentCenter.zoom != null) {
          this.filter.zoomLevel = storedState.currentCenter.zoom;
        }
      }
      this.isInitial = false;
    } else {
      return false;
    }
    if (storedState.filterDataObject?.selectedLocation) {
      const savedLocation = storedState.filterDataObject.selectedLocation;
      this.city = {
        city: savedLocation.address,
        lat: savedLocation.lat,
        lng: savedLocation.lng,
        enableZoomFallBack: true
      };
    }
    this.restoredFromReturnNavigation = hasFilters;
    return true;
  }

  private persistListingViewState() {
    this.businessService.saveListingState({
      activePanel: this.activePanel,
      gridView: this.gridView,
      currentCenter: this.currentCenter,
      searchIds: this.searchIds
    });
  }
}
